/**
 * The fleet-wide emergency controls: recover everything, or cut everything.
 *
 * This app can plan and fly aggressive profiles but had no single decisive
 * "stop" — the per-drone force-disarm was plumbed and never reachable. These two
 * controls are opposites and are drawn as opposites:
 *
 *   RTL ALL  — recover: send every controllable airborne aircraft home. One
 *              press behind a one-step confirm, because it interrupts every
 *              mission at once.
 *   KILL ALL — cut motors on every controllable armed aircraft. An airborne
 *              multirotor force-disarmed FALLS, so this demands the operator
 *              type KILL, mirroring the reference's typed-KILL and Planning's
 *              typed-callsign confirm. Danger red, unmistakable.
 *
 * Three things here are safety features, not polish, and match the tactical
 * dock's own three:
 *   - a re-entrancy guard in a ref, so a double-press cannot fire twice (two
 *     clicks in one frame both read the old value);
 *   - honest per-aircraft reporting via fanOut — "KILLED 4 · 2 REFUSED", never a
 *     flat success when some refused;
 *   - it only ever acts on the CONTROLLABLE set (operator role, and this drone
 *     in scope), and says so plainly when nothing is airborne or armed rather
 *     than pretending to fire.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ApiResult } from '@/api/client';
import { droneCommands, fanOut } from '@/api/drones';
import { useAuthStore, useCanCommand } from '@/stores/authStore';
import { useDroneStore } from '@/stores/droneStore';
import type { CommandResult, DroneId } from '@/types/domain';

/** Same window the tactical dock's DISARM confirm uses, for one muscle memory. */
const CONFIRM_WINDOW_MS = 4000;

/** Airborne is the tactical dock's own threshold: above 1 m AGL is flying. */
const AIRBORNE_M = 1;

interface Outcome {
  droneId: DroneId;
  ok: boolean;
  message: string;
}

interface Report {
  verb: string;
  outcomes: Outcome[];
}

/**
 * The controllable subsets to act on, recomputed only when membership or an
 * armed/airborne bit actually flips.
 *
 * The store ticks at 10 Hz, so the trigger is a primitive key (a change to it is
 * the only thing that re-runs this) and the sets are read fresh from state when
 * it does. Reading altitude straight into a boolean means a climbing aircraft
 * does not re-render this panel on every centimetre.
 */
function useEmergencyTargets(): { rtl: DroneId[]; kill: DroneId[] } {
  const canCommand = useCanCommand();
  const controllable = useAuthStore((s) => s.controllableDroneIds);

  const fleetKey = useDroneStore((s) =>
    s.ids
      .map((id) => {
        const drone = s.drones.get(id);
        const armed = drone?.scalars.armed === true ? '1' : '0';
        const airborne = (drone?.scalars.altRelative ?? 0) > AIRBORNE_M ? '1' : '0';
        return `${id}=${armed}${airborne}`;
      })
      .join(','),
  );

  return useMemo(() => {
    const rtl: DroneId[] = [];
    const kill: DroneId[] = [];
    if (!canCommand) return { rtl, kill };
    const { ids, drones } = useDroneStore.getState();
    for (const id of ids) {
      // controllableDroneIds === null means the role controls every aircraft.
      if (controllable !== null && !controllable.includes(id)) continue;
      const drone = drones.get(id);
      if (!drone) continue;
      const armed = drone.scalars.armed === true;
      const airborne = (drone.scalars.altRelative ?? 0) > AIRBORNE_M;
      if (airborne) rtl.push(id);
      // A grounded-but-armed aircraft still has live motors to cut.
      if (armed || airborne) kill.push(id);
    }
    return { rtl, kill };
    // fleetKey is the reactive trigger; the sets are read from fresh state above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetKey, controllable, canCommand]);
}

function summarize(verb: string, outcomes: Outcome[]): string {
  const refused = outcomes.filter((o) => !o.ok).length;
  const accepted = outcomes.length - refused;
  return refused ? `${verb} ${accepted} · ${refused} REFUSED` : `${verb} ${accepted}`;
}

export function EmergencyControls() {
  const canCommand = useCanCommand();
  const { rtl: rtlTargets, kill: killTargets } = useEmergencyTargets();

  const [busy, setBusy] = useState<'rtl' | 'kill' | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [confirmRtl, setConfirmRtl] = useState(false);
  const [killArming, setKillArming] = useState(false);
  const [killInput, setKillInput] = useState('');
  // Guard in a ref, not state: two clicks in one frame both read the old state
  // value and both pass, which is exactly how a double-tap fired twice before.
  const inFlight = useRef(false);

  // The RTL confirm lapses on its own, so a half-pressed "RTL 6 aircraft?" left
  // on screen is not a live one-click-away command minutes later.
  useEffect(() => {
    if (!confirmRtl) return undefined;
    const timer = window.setTimeout(() => setConfirmRtl(false), CONFIRM_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [confirmRtl]);

  const fire = useCallback(
    async (
      kind: 'rtl' | 'kill',
      verb: string,
      ids: DroneId[],
      run: (id: DroneId) => Promise<ApiResult<CommandResult>>,
    ) => {
      if (inFlight.current || ids.length === 0) return;
      inFlight.current = true;
      setBusy(kind);
      setReport(null);
      try {
        const results = await fanOut(ids, run);
        setReport({
          verb,
          outcomes: results.map(({ droneId, result }) => ({
            droneId,
            ok: result.ok,
            message: result.ok ? result.data.message : result.error.message,
          })),
        });
      } finally {
        inFlight.current = false;
        setBusy(null);
      }
    },
    [],
  );

  // Only an operator with aircraft in scope has any emergency authority; the
  // server refuses anyone else regardless, so there is nothing to draw for them.
  if (!canCommand) return null;

  const killMatch = killInput.trim().toUpperCase() === 'KILL';
  const idle = busy === null;

  const runRtl = () => {
    if (!idle || rtlTargets.length === 0) return;
    if (!confirmRtl) {
      setConfirmRtl(true);
      return;
    }
    setConfirmRtl(false);
    void fire('rtl', 'RTL SENT', rtlTargets, (id) => droneCommands.returnToLaunch(id));
  };

  const runKill = () => {
    // The typed-KILL gate, enforced here and not only by disabling the button:
    // the kill path must not fire without the word, whatever the UI state.
    if (!idle || !killMatch || killTargets.length === 0) return;
    setKillArming(false);
    setKillInput('');
    void fire('kill', 'KILLED', killTargets, (id) => droneCommands.forceDisarm(id));
  };

  const nothingLive = rtlTargets.length === 0 && killTargets.length === 0;

  return (
    <section
      aria-label="Fleet emergency controls"
      className="glass flex w-[248px] flex-col gap-2 rounded-[16px] p-3"
    >
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]" style={{ letterSpacing: '0.1em' }}>
          EMERGENCY
        </span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {killTargets.length} ACTIVE
        </span>
      </div>

      <button
        type="button"
        onClick={runRtl}
        disabled={!idle || rtlTargets.length === 0}
        aria-label={
          confirmRtl
            ? `Confirm return to launch for ${rtlTargets.length} aircraft`
            : `Return all ${rtlTargets.length} airborne aircraft to launch`
        }
        className="rounded-[99px] px-3 py-2.5 label-mono text-[10px] font-semibold transition-colors disabled:opacity-35"
        style={{
          letterSpacing: '0.06em',
          background: confirmRtl
            ? 'color-mix(in oklch, var(--caution) 22%, transparent)'
            : 'oklch(1 0 0 / 0.08)',
          border: `1px solid ${
            confirmRtl
              ? 'color-mix(in oklch, var(--caution) 60%, transparent)'
              : 'var(--border)'
          }`,
          color: confirmRtl ? 'var(--caution)' : 'var(--foreground)',
        }}
      >
        {busy === 'rtl'
          ? 'RECALLING…'
          : confirmRtl
            ? `RTL ${rtlTargets.length} AIRCRAFT?`
            : `↩ RTL ALL · ${rtlTargets.length}`}
      </button>

      {!killArming ? (
        <button
          type="button"
          onClick={() => {
            if (idle && killTargets.length > 0) setKillArming(true);
          }}
          disabled={!idle || killTargets.length === 0}
          aria-label={`Kill all ${killTargets.length} armed aircraft, cutting motors`}
          className="rounded-[99px] px-3 py-2.5 label-mono text-[10px] font-semibold transition-colors disabled:opacity-35"
          style={{
            letterSpacing: '0.06em',
            background: 'color-mix(in oklch, var(--danger) 26%, transparent)',
            border: '1px solid color-mix(in oklch, var(--danger) 70%, transparent)',
            color: 'var(--danger-foreground)',
          }}
        >
          {busy === 'kill' ? 'CUTTING MOTORS…' : `⏻ KILL ALL · ${killTargets.length}`}
        </button>
      ) : (
        <div
          className="flex flex-col gap-2 rounded-[12px] p-2.5"
          style={{
            background: 'color-mix(in oklch, var(--danger) 12%, transparent)',
            border: '1px solid color-mix(in oklch, var(--danger) 55%, transparent)',
          }}
        >
          <p className="text-[10.5px] leading-snug" style={{ color: 'var(--danger-foreground)' }}>
            Cuts motors on {killTargets.length} aircraft. Aircraft in flight will fall.
          </p>
          <label
            className="label-mono text-[8px]"
            style={{ color: 'var(--danger-foreground)' }}
            htmlFor="kill-confirm-input"
          >
            TYPE KILL TO CONFIRM
          </label>
          <input
            id="kill-confirm-input"
            value={killInput}
            onChange={(event) => setKillInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') runKill();
            }}
            autoComplete="off"
            aria-label="Type KILL to confirm cutting all motors"
            className="well tnum h-8 rounded-[8px] px-2.5 text-center text-[12px] outline-none"
            style={{ letterSpacing: '0.2em' }}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={runKill}
              disabled={!idle || !killMatch}
              aria-label="Confirm kill all"
              className="flex-1 rounded-[99px] px-3 py-2 label-mono text-[10px] font-semibold transition-colors disabled:opacity-35"
              style={{
                letterSpacing: '0.06em',
                background: 'var(--danger)',
                color: 'var(--danger-foreground)',
              }}
            >
              CONFIRM KILL
            </button>
            <button
              type="button"
              onClick={() => {
                setKillArming(false);
                setKillInput('');
              }}
              className="rounded-[99px] border border-[color:var(--border)] px-3 py-2 label-mono text-[10px] text-[color:var(--muted-foreground-2)] hover:text-foreground"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {nothingLive ? (
        <p className="text-[9.5px] leading-snug text-[color:var(--muted-foreground-3)]">
          No controllable aircraft are airborne or armed.
        </p>
      ) : null}

      {report ? (
        <div className="flex flex-col gap-1 rounded-[12px] border border-[color:var(--border-nested)] p-2">
          <span
            className="label-mono text-[8.5px]"
            style={{
              color: report.outcomes.some((o) => !o.ok)
                ? 'var(--danger-foreground)'
                : 'var(--ok)',
            }}
          >
            {summarize(report.verb, report.outcomes)}
          </span>
          <ul className="scroll-thin flex max-h-24 flex-col gap-0.5 overflow-y-auto">
            {report.outcomes.map((outcome) => (
              <li key={outcome.droneId} className="flex items-baseline gap-1.5 text-[10px]">
                <span
                  className="shrink-0"
                  style={{ color: outcome.ok ? 'var(--ok)' : 'var(--danger-foreground)' }}
                >
                  {outcome.ok ? '✓' : '✕'}
                </span>
                <span className="tnum w-16 shrink-0 truncate">{outcome.droneId}</span>
                <span
                  className="min-w-0 flex-1 truncate text-[color:var(--muted-foreground-2)]"
                  style={outcome.ok ? undefined : { color: 'var(--danger-foreground)' }}
                >
                  {outcome.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default EmergencyControls;
